import React from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { useUserSystems } from '@/hooks/useUserSystems';
import { useCompanyActiveSystems } from '@/hooks/useCompanyActiveSystems';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DefaultSystemModal } from '@/components/DefaultSystemModal';
import { useTheme } from '@/contexts/theme-provider';
import { ChevronDown, ExternalLink, User, Settings, LogOut, Building2, Sun, Moon, Monitor } from 'lucide-react';

export function UserHeaderMenu() {
  const { user, profile, signOut, companies } = useAuth();
  const { data: globalSystems = [] } = useUserSystems();
  const { setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  // Detect if we're in a company system context
  const isInCompanySystem = location.pathname.match(/^\/company\/([^\/]+)\/systems\/([^\/]+)/);
  const companySlug = isInCompanySystem ? params.slug : null;
  const company = companies.find(c => c.slug === companySlug);
  
  // Use company-specific systems if in company context, otherwise use global systems
  const { data: companySystems = [] } = useCompanyActiveSystems(company?.id);
  const systems = isInCompanySystem ? companySystems : globalSystems;

  if (!user) return null;

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const userName = profile?.full_name || user.email?.split('@')[0] || 'Usuário';
  const userInitial = (profile?.full_name?.charAt(0) || user.email?.charAt(0) || 'U').toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex items-center space-x-2 h-auto px-3 py-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="text-sm font-medium">
              {userInitial}
            </AvatarFallback>
          </Avatar>
          <div className="hidden md:flex flex-col items-start text-left">
            <span className="text-sm font-medium leading-none">{userName}</span>
            <span className="text-xs text-muted-foreground leading-none mt-0.5">
              {user.email}
            </span>
          </div>
          <ChevronDown className="ml-1 h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-64">
        {/* User Info Section */}
        <DropdownMenuLabel className="pb-2">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
          </div>
        </DropdownMenuLabel>
        
        {/* Company Dashboard Button - Show when in company system */}
        {isInCompanySystem && company && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={`/company/${company.slug}/admin`} className="flex items-center w-full">
                <Building2 className="mr-2 h-4 w-4" />
                <span>Dashboard da Empresa</span>
              </Link>
            </DropdownMenuItem>
          </>
        )}

        
        <DropdownMenuSeparator />
        
        {/* Profile and Settings */}
        <DropdownMenuItem asChild>
          <Link to="/profile" className="flex items-center w-full">
            <User className="mr-2 h-4 w-4" />
            <span>Perfil</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuItem asChild>
          <Link to="/settings" className="flex items-center w-full">
            <Settings className="mr-2 h-4 w-4" />
            <span>Configuração</span>
          </Link>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Theme Selection */}
        <DropdownMenuItem onClick={() => setTheme("light")}>
          <Sun className="mr-2 h-4 w-4" />
          <span>Tema Claro</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => setTheme("dark")}>
          <Moon className="mr-2 h-4 w-4" />
          <span>Tema Escuro</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => setTheme("system")}>
          <Monitor className="mr-2 h-4 w-4" />
          <span>Sistema</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        
        {/* Logout */}
        <DropdownMenuItem 
          onClick={handleSignOut}
          className="text-red-600 focus:text-red-600 focus:bg-red-50"
        >
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sair</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}